import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faPlus, faUsers, faLink } from '@fortawesome/free-solid-svg-icons';
import AddContactPage from '../../AddContactPage/AddContactPage';
import EditContactPage from '../../EditContactPage/EditContactPage';
import LinkExistingContactModal from '../../LinkExistingContactModal/LinkExistingContactModal';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';

interface KeyContactsCardProps {
  getInitials: (name: string) => string;
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData?: any;
  onContactAdded?: () => void;
}

interface Contact {
  id: string;
  name: string;
  title: string;
  isPrimary?: boolean;
  contactType: 'client' | 'agent';
  email?: string;
  phone?: string;
  company?: string;
  address?: string;
}

const KeyContactsCard: React.FC<KeyContactsCardProps> = ({ 
  getInitials, 
  stakeholderId, 
  type,
  stakeholderData,
  onContactAdded 
}) => {
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [isLinkExistingModalOpen, setIsLinkExistingModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContacts = async () => {
      if (type === 'organization' && stakeholderData?.Company) {
        try {
          setLoading(true);
          const allClients = await ClientService.getClients();
          const allAgents = await AgentsService.getAgents();
          
          const companyName = stakeholderData.Company;
          
          // Get all clients with this company name
          const orgClients = allClients.filter((c: any) => c.Company === companyName);
          
          // Get all agents with this company name
          const orgAgents = allAgents.filter((a: any) => a.Company === companyName);
          
          // Combine and format contacts
          const contactList: Contact[] = [];
          
          // Add clients
          orgClients.forEach((client: any, index: number) => {
            const fullName = `${client.FirstName || ''} ${client.LastName || ''}`.trim();
            if (fullName) {
              contactList.push({
                id: client.ClientID,
                name: fullName,
                title: client.Title || '',
                isPrimary: index === 0, // First contact is primary
                contactType: 'client',
                email: client.Email,
                phone: client.Phone,
                company: client.Company,
                address: client.Address,
              });
            }
          });
          
          // Add agents
          orgAgents.forEach((agent: any) => {
            const fullName = `${agent.FirstName || ''} ${agent.LastName || ''}`.trim();
            if (fullName) {
              // Check if this agent is already in the list (avoid duplicates)
              const exists = contactList.some(c => c.name === fullName);
              if (!exists) {
                contactList.push({
                  id: agent.AgentID,
                  name: fullName,
                  title: agent.Title || '',
                  isPrimary: false, // Primary is typically the first client
                  contactType: 'agent',
                  email: agent.Email,
                  phone: agent.Phone,
                  company: agent.Company,
                  address: agent.Address,
                });
              }
            }
          });
          
          setContacts(contactList);
        } catch (error) {
          console.error('Error fetching contacts:', error);
          setContacts([]);
        } finally {
          setLoading(false);
        }
      } else {
        // For individual stakeholders, no contacts to show
        setContacts([]);
        setLoading(false);
      }
    };

    fetchContacts();
  }, [type, stakeholderData, stakeholderId]);

  const handleAddContact = () => {
    setIsAddContactModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddContactModalOpen(false);
  };

  const handleContactAdded = () => {
    if (onContactAdded) {
      onContactAdded();
    }
    // Refresh the page to show the new contact
    window.location.reload();
  };

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact);
    setIsEditContactModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditContactModalOpen(false);
    setSelectedContact(null);
  };

  const handleContactUpdated = async (primaryContactId?: string) => {
    // Refresh contacts list to reflect updates
    if (type === 'organization' && stakeholderData?.Company) {
      try {
        const allClients = await ClientService.getClients();
        const allAgents = await AgentsService.getAgents();
        
        const companyName = stakeholderData.Company;
        
        // Get all clients with this company name
        const orgClients = allClients.filter((c: any) => c.Company === companyName);
        
        // Get all agents with this company name
        const orgAgents = allAgents.filter((a: any) => a.Company === companyName);
        
        // Combine and format contacts
        const contactList: Contact[] = [];
        
        // Add clients
        orgClients.forEach((client: any) => {
          const fullName = `${client.FirstName || ''} ${client.LastName || ''}`.trim();
          if (fullName) {
            contactList.push({
              id: client.ClientID,
              name: fullName,
              title: client.Title || '',
              isPrimary: false, // Will be set based on primaryContactId
              contactType: 'client',
              email: client.Email,
              phone: client.Phone,
              company: client.Company,
              address: client.Address,
            });
          }
        });
        
        // Add agents
        orgAgents.forEach((agent: any) => {
          const fullName = `${agent.FirstName || ''} ${agent.LastName || ''}`.trim();
          if (fullName) {
            // Check if this agent is already in the list (avoid duplicates)
            const exists = contactList.some(c => c.name === fullName);
            if (!exists) {
              contactList.push({
                id: agent.AgentID,
                name: fullName,
                title: agent.Title || '',
                isPrimary: false, // Will be set based on primaryContactId
                contactType: 'agent',
                email: agent.Email,
                phone: agent.Phone,
                company: agent.Company,
                address: agent.Address,
              });
            }
          }
        });
        
        // If a primary contact ID was provided, reorder the list to put that contact first
        if (primaryContactId) {
          const primaryIndex = contactList.findIndex(c => c.id === primaryContactId);
          if (primaryIndex > 0) {
            // Move the primary contact to the first position
            const primaryContact = contactList.splice(primaryIndex, 1)[0];
            contactList.unshift(primaryContact);
          }
        }
        
        // Set the first contact as primary
        if (contactList.length > 0) {
          contactList[0].isPrimary = true;
          // Unset primary for all other contacts
          for (let i = 1; i < contactList.length; i++) {
            contactList[i].isPrimary = false;
          }
        }
        
        setContacts(contactList);
      } catch (error) {
        console.error('Error refreshing contacts:', error);
        // Fallback to page reload
        window.location.reload();
      }
    } else {
      window.location.reload();
    }
    
    if (onContactAdded) {
      onContactAdded();
    }
  };

  return (
    <>
      <div className="stakeholder-details-card">
        <div className="stakeholder-card-header">
          <div className="stakeholder-card-header-left">
            <FontAwesomeIcon icon={faUsers} className="stakeholder-card-icon" />
            <h3 className="stakeholder-card-title">
              Key Contacts ({loading ? '...' : contacts.length})
            </h3>
          </div>
          {type === 'organization' && (
            <div className="stakeholder-card-header-actions">
              <button
                className="stakeholder-add-button"
                onClick={() => setIsLinkExistingModalOpen(true)}
                aria-label="Link an existing contact to this organization"
              >
                <FontAwesomeIcon icon={faLink} />
                Link Existing
              </button>
              <button className="stakeholder-add-button" onClick={handleAddContact}>
                <FontAwesomeIcon icon={faPlus} />
                Add Contact
              </button>
            </div>
          )}
        </div>
        <div className="stakeholder-card-content">
          {loading ? (
            <div className="stakeholder-contacts-loading">Loading contacts...</div>
          ) : contacts.length === 0 ? (
            <div className="stakeholder-contacts-empty">
              {type === 'organization' 
                ? 'No contacts found for this organization.' 
                : 'No contacts to display for individual stakeholders.'}
            </div>
          ) : (
            <div className="stakeholder-contacts-list">
              {contacts.map((contact) => (
                <div 
                  key={contact.id} 
                  className="stakeholder-contact-person"
                  onClick={() => handleContactClick(contact)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="stakeholder-contact-avatar">
                    {getInitials(contact.name)}
                  </div>
                  <div className="stakeholder-contact-person-info">
                    <div className="stakeholder-contact-person-name">
                      {contact.name}
                      {contact.isPrimary && (
                        <span className="stakeholder-contact-primary-badge">Primary</span>
                      )}
                    </div>
                    <div className="stakeholder-contact-person-title">
                      {contact.title || 'No title'}
                    </div>
                  </div>
                  <FontAwesomeIcon icon={faChevronRight} className="stakeholder-contact-arrow" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {isAddContactModalOpen && (
        <AddContactPage
          onClose={handleCloseModal}
          stakeholderId={stakeholderId}
          type={type}
          stakeholderData={stakeholderData}
          onContactAdded={handleContactAdded}
        />
      )}
      {isEditContactModalOpen && selectedContact && (
        <EditContactPage
          onClose={handleCloseEditModal}
          contact={selectedContact}
          stakeholderId={stakeholderId}
          type={type}
          stakeholderData={stakeholderData}
          allContacts={contacts}
          onContactUpdated={handleContactUpdated}
        />
      )}
      {isLinkExistingModalOpen && type === 'organization' && stakeholderData?.Company && (
        <LinkExistingContactModal
          isOpen={isLinkExistingModalOpen}
          onClose={() => setIsLinkExistingModalOpen(false)}
          companyName={stakeholderData.Company}
          onContactsLinked={() => {
            // Refresh the contacts list in place using the existing refetch helper.
            handleContactUpdated();
          }}
        />
      )}
    </>
  );
};

export default KeyContactsCard;

